import ImagePlaceholder from "assets/icons/imagePlaceholder.svg"
import { DetailedProductFieldsFragment } from "client/graphql/storefront-api"
import { Dispatch, SetStateAction } from "react"
import { buttonFocus } from "shared/styles/mixins"
import useArrayElement from "shared/utils/useArrayElement"
import styled from "styled-components"

import { scaleShopifyImageUrl } from "../../utils"

export const CarouselWrapper = styled.div`
  width: 100%;
  height: 100%;
`

export const MAIN_IMAGE_HEIGHT = 190

export const MainImage = styled.div`
  height: ${MAIN_IMAGE_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    border-radius: 8px;
    height: 100%;
  }

  img,
  svg {
    max-width: 90%;
    max-height: 100%;
    object-fit: cover;
  }
`

export const Thumbnails = styled.div`
  width: 100%;
  height: 56px;
  margin: 15px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const THUMBNAIL_IMAGE_HEIGHT = 56

export const ImageThumbnailButton = styled.button<{
  $isSelected: boolean
}>`
  position: relative;
  margin: 0 8px;
  border-radius: 4px;
  background-color: transparent;
  border: none;
  padding: 0;
  appearance: none;
  overflow: hidden;
  max-height: 100%;
  box-shadow: ${(props) =>
    props.$isSelected
      ? `0 0 0px 2px white, 0 0 0px 4px ${props.theme.colors.darkMed}`
      : "none"};

  img {
    height: 56px;
    width: 56px;
    object-fit: cover;
  }

  ${buttonFocus}
`

export const ViewGalleryCover = styled.div`
  background-color: rgba(0, 0, 0, 0.4);
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  color: white;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
`

export type ImageCarouselProps = {
  product: DetailedProductFieldsFragment
  openImageGallery: Dispatch<SetStateAction<number>>
  thumbnailCount?: number
}

const ImageCarousel = ({
  product,
  openImageGallery,
  thumbnailCount = 4,
}: ImageCarouselProps) => {
  const [mainImage, { index: imgIndex, setIndex }] = useArrayElement(
    product.images.nodes,
  )

  return (
    <>
      <MainImage>
        {mainImage?.url ? (
          <img
            height={MAIN_IMAGE_HEIGHT}
            alt={product.title ?? ""}
            src={mainImage?.url}
          />
        ) : (
          <ImagePlaceholder />
        )}
      </MainImage>

      {product.images.nodes.length > 1 && (
        <Thumbnails>
          {product.images.nodes.map((image, index) => {
            if (index < thumbnailCount) {
              return (
                <ImageThumbnailButton
                  key={image.id}
                  type="button"
                  onClick={() => setIndex(index)}
                  $isSelected={index === imgIndex}
                >
                  <img
                    alt={product.title}
                    src={scaleShopifyImageUrl(image?.url)}
                  />
                </ImageThumbnailButton>
              )
            }
            return null
          })}
          {product.images.nodes.length > thumbnailCount && (
            <ImageThumbnailButton
              key={product.images.nodes[thumbnailCount].id}
              type="button"
              onClick={() => openImageGallery(thumbnailCount)}
              $isSelected={false}
            >
              <ViewGalleryCover>
                +{product.images.nodes.length - thumbnailCount}
              </ViewGalleryCover>
              <img
                alt="View Gallery"
                src={product.images.nodes[thumbnailCount]?.url}
              />
            </ImageThumbnailButton>
          )}
        </Thumbnails>
      )}
    </>
  )
}

export default ImageCarousel
